import React, { useState } from "react";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import themeCreator from "@Themes/themeCreator";
import PageTitle from "@Components/PageTitle/PageTitle";
import { Container } from "@mui/material";
import DateSelector from "@Components/DateTimePickers/DatePicker";
import useSendPostRequest from "@Hooks/useSendPostRequest";
import { APPROVALS_URL } from "../../API/APIRoutes";
import DropDown from "../../Components/DropDown/DropDown";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useDispatch } from "react-redux";

import {
  OptionalDropDownValidation,
  pastOrNowDateValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import { Grid, Typography, TextField, Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { FormStyle } from "../Styles/FormStyle";
import { useParams } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Joi from "joi";
import { validateForm } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import axios from "axios";
import { formatDateUTC } from "../../Utils/Validations/HelperFunctions/FormaterFunctions";
import UploadFileButton from "../../Components/Buttons/uploadFileButton";
import { decryptId, encryptId } from "../../Utils/SecurityFunctions";

export default function AddApproval() {
  const orderId = decryptId(useParams().orderId);
  const theme = themeCreator();
  const navigate = useNavigate();
  const approvalNames = [
    { id: "Select Approval Name", name: "Select Approval Name" },
    { id: "Import_Permit", name: "Import Permit" },
    { id: "Veterinary_import_Permit", name: "Veterinary import Permit" },
    { id: "Agriculture_Import_Permit", name: "Agriculture Import Permit" },
    { id: "Medical_Customs_Release", name: "Medical Customs Release" },
  ];
  const approvalStates = [
    { id: "Select Approval State", name: "Select Approval State" },
    { id: "PENDING", name: "Pending" },
    { id: "APPROVED", name: "Approved" },
    { id: "REJECTED", name: "Rejected" },
    { id: "PENDING_HEALTH_CERTIFICATE", name: "Pending Health Certificate" },
  ];

  const initialApproval = {
    approvalName: "Select Approval Name",
    approvalState: "Select Approval State",
    receivedDate: null,
    submissionDate: null,
    comment: "",
  };
  const validationSchema = Joi.object({
    receivedDate: Joi.alternatives()
      .try(pastOrNowDateValidation, Joi.allow(null))
      .optional(),
    submissionDate: Joi.date().required().messages({
      "any.required": "Submission Date is required",
    }),
    approvalName: OptionalDropDownValidation("Approval Name"),
    approvalState: OptionalDropDownValidation("Approval State"),
    comment: Joi.string().allow(""),
    orderId: Joi.string().required(),
    isFileAttached: Joi.boolean(),
  });
  const [approval, setApproval] = useState(initialApproval);
  const [approvalFile, setApprovalFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [checked, setChecked] = useState(false);
  const [errors, setErrors] = useState({
    approvalName: "",
    approvalState: "",
    receivedDate: "",
    submissionDate: "",
    comment: "",
  });
  const { isLoading: isloading, requestMethod: postData } =
    useSendPostRequest(true);

  const onDatePick = (newValue) => {
    setApproval({ ...approval, receivedDate: newValue });
    setErrors({ ...errors, receivedDate: "" });
  };
  const onSubmissionDatePick = (newValue) => {
    setApproval({ ...approval, submissionDate: newValue });
    setErrors({ ...errors, submissionDate: "" });
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setApprovalFile(file);
      setFileName(file.name);
    }
  };

  const clearData = () => {
    setApproval(initialApproval);
    setApprovalFile(null);
    setFileName("");
    setErrors({
      approvalName: "",
      approvalState: "",
      receivedDate: "",
      submissionDate: "",
      comment: "",
    });
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault(); // Add this to prevent form submission

    // Format all dates in the invoice and payments
    const formattedApproval = {
      ...approval,
      receivedDate: formatDateUTC(approval.receivedDate),
      submissionDate: formatDateUTC(approval.submissionDate),
      orderId: orderId,
      isFileAttached: approvalFile ? true : false,
    };

    if (validateForm(formattedApproval, setErrors, validationSchema)) {
      const response = await postData(
        APPROVALS_URL,
        formattedApproval,
        null,
        "Approval has been added successfully."
      );
      if (response) {
        if (!response.data.preSignedUrl && approvalFile) {
          throw new Error("Presigned URL not received");
        }
        const { preSignedUrl, objectKey } = response.data;

        // Upload file to Minio
        if (approvalFile) {
          await axios.put(preSignedUrl, approvalFile, {
            headers: {
              "Content-Type": "application/pdf",
            },
          });
        }
        clearData();
        sessionStorage.setItem("openSectionIndex", 0);
        navigate(
          DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId))
        );
      }
      // Clear the form data
      clearData();
    }
  };

  const form = (
    <>
      <form onSubmit={handleOnSubmit} className="formWrapper">
        <Grid
          className="inputContainer"
          container
          spacing={2}
          justifyContent="flex-end"
        >
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Approval Name
            </Typography>
            <DropDown
              state={approval.approvalName}
              setState={(value) => {
                setApproval({ ...approval, approvalName: value });
                setErrors({ ...errors, approvalName: "" }); // Clear error message on selection
              }}
              name="approval Name"
              label=""
              items={approvalNames}
              placeholder="Select Approval Name"
              helperText={errors.approvalName}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Approval State
            </Typography>
            <DropDown
              state={approval.approvalState}
              setState={(value) => {
                setApproval({ ...approval, approvalState: value });
                setErrors({ ...errors, approvalState: "" }); // Clear error message on selection
              }}
              name="approval State"
              label=""
              items={approvalStates}
              placeholder="Select Approval State"
              helperText={errors.approvalState}
            />
          </Grid>
          <Grid item xs={12}>
            <DateSelector
              name={"Submission Date"}
              value={approval.submissionDate}
              error={errors.submissionDate}
              onPick={onSubmissionDatePick}
            />
          </Grid>
          <Grid item xs={12}>
            <DateSelector
              name={"Received Date"}
              value={approval.receivedDate}
              error={errors.receivedDate}
              onPick={onDatePick}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Comment (Optional)
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              name="comment"
              type="text"
              placeholder="Comment"
              value={approval.comment}
              onChange={(e) =>
                setApproval({ ...approval, comment: e.target.value })
              }
            />
          </Grid>
          <Box
            className="submissionBox"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <UploadFileButton
                handleFileChange={handleFileChange}
                fileName={approvalFile?.name}
                acceptedTypes={["application/pdf"]}
                buttonLabel={
                  approvalFile ? "Change File" : "Upload Approval File"
                }
                buttonVariant="outlined"
                buttonStartIcon={
                  approvalFile ? <AttachFileIcon /> : <CloudUploadIcon />
                }
                dragAndDrop={true}
                fullWidth={true}
              />

              <Button
                type="submit"
                variant="contained"
                className="submissionButton"
                size="large"
                disabled={isloading}
              >
                {!isloading ? (
                  "Add Approval"
                ) : (
                  <LoadingComponent size="2em" thickness={5} color="white" />
                )}
              </Button>
            </Box>

            {fileName && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                <AttachFileIcon
                  fontSize="small"
                  color="primary"
                  sx={{ mr: 0.5 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {fileName}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </form>
    </>
  );
  return (
    <FormStyle className="PageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title={"New Approval"}
            PageIcon={<VerifiedIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          ></PageTitle>
        </Box>
        <Box sx={{ pt: 3 }}>{form}</Box>
      </Container>
    </FormStyle>
  );
}
